import { EntityCard } from "./EntityCard";
import { EntityTypeBadge } from "./entityTypeMeta";
import { Badge } from "../../base/badge";
import { ChevronRight } from "lucide-react";
import { cn } from "@charphq/ui/lib/utils";

interface Project {
  id: string;
  name: string;
  description?: string | null;
  status?: string;
}

interface ProjectCardProps {
  project: Project;
  onClick?: () => void;
  className?: string;
  /** Highlight the card as the active keyboard selection. */
  isSelected?: boolean;
}

const getStatusVariant = (status?: string) => {
  switch (status?.toLowerCase()) {
    case "active":
      return "default" as const;
    case "completed":
      return "secondary" as const;
    case "cancelled":
      return "destructive" as const;
    default:
      return "outline" as const;
  }
};

export function ProjectCard({ project, onClick, className, isSelected }: ProjectCardProps) {
  return (
    <EntityCard
      onClick={onClick}
      className={cn(className, isSelected && "border-primary ring-2 ring-primary/50 shadow-md")}
    >
      <div className="flex h-full items-center justify-between">
        {/* Left side - Main content */}
        <div className="flex-1 min-w-0">
          {/* Top row: type badge, name, status */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 min-w-0">
              <EntityTypeBadge kind="project" />
              <h3 className="font-medium text-base truncate">{project.name}</h3>
            </div>
            {project.status && (
              <Badge variant={getStatusVariant(project.status)} className="text-xs shrink-0 ml-2">
                {project.status}
              </Badge>
            )}
          </div>

          {/* Second row: description */}
          {project.description && (
            <p className="text-sm text-muted-foreground truncate">{project.description}</p>
          )}
        </div>

        {/* Right side - Chevron */}
        <div className="flex flex-col items-end gap-2 shrink-0 ml-4">
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </div>
      </div>
    </EntityCard>
  );
}
